import React, { useMemo } from 'react';
import { darken } from 'polished';
import { useTheme } from 'styled-components';
import { IconInfo } from '../icons/info';
import { IconSuccess } from '../icons/success';
import { IconWarning } from '../icons/warning';
import { IconError } from '../icons/error';
import { IconSpinner } from '../icons/spinner/spinner';
import { IconClose } from '../icons/close';
import { GapSizeType } from '../../../theme/type/gap-type';
import { cn, marginBottomClass, marginTopClass } from '../../../utils/tailwind';

interface AlertPropsInterface {
    children: React.ReactNode;
    type?: 'success' | 'error' | 'warning' | 'info';
    size?: 'small' | 'medium' | 'large';
    icon?: boolean;
    marginBottom?: GapSizeType;
    marginTop?: GapSizeType;
    loading?: boolean;
    onCloseClick?: () => void;
    clickableIcon?: boolean;
    onClickIcon?: () => void;
}

export const Alert: React.FC<AlertPropsInterface> = ({
    children,
    type = 'info',
    icon,
    marginTop,
    marginBottom = 'vsmall',
    loading,
    onCloseClick,
    size = 'medium',
    clickableIcon = false,
    onClickIcon,
}: AlertPropsInterface) => {
    const theme = useTheme();

    const iconComponent = useMemo(() => {
        if (type === 'success') {
            return <IconSuccess />;
        }

        if (type === 'warning') {
            return <IconWarning />;
        }

        if (type === 'error') {
            return <IconError />;
        }
        return <IconInfo />;
    }, []);

    return (
        <div
            className={cn(
                'relative flex items-center rounded-medium text-left font-bold',
                'px-vsmall pr-medium py-vsmall',
                '[_i]:h-[1.6rem] [_i]:w-[1.6rem]',
                size === 'small' && 'px-[0.8rem] py-[0.5rem] text-[1.2rem]',
                size === 'large' &&
                    'px-small pr-medium py-vsmall text-xlarge font-medium leading-xlarge [_i]:h-[2.6rem] [_i]:w-[2.6rem]',
                marginBottomClass[marginBottom],
                marginTop && marginTopClass[marginTop],
            )}
            style={{ backgroundColor: theme.alert[type].background, color: theme.alert[type].color }}
        >
            {icon && !loading && (
                <div
                    className={cn('mr-xsmall flex items-center', clickableIcon && 'cursor-pointer')}
                    onClick={() => {
                        if (clickableIcon && onClickIcon) {
                            onClickIcon();
                        }
                    }}
                >
                    {iconComponent}
                </div>
            )}
            {loading && (
                <div className="mr-xsmall flex items-center">
                    <IconSpinner />
                </div>
            )}
            <div
                className="flex-1 [&_a]:m-0 [&_a]:cursor-pointer [&_a]:text-[var(--alert-link)] [&_a]:underline hover:[&_a]:no-underline"
                style={{ '--alert-link': darken(0.2, theme.alert[type].color) } as React.CSSProperties}
            >
                {children}
            </div>
            {onCloseClick && (
                <div
                    className="absolute right-0 top-0 ml-large flex cursor-pointer items-center p-xsmall [&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:opacity-50 hover:[&_i]:opacity-100"
                    onClick={() => {
                        if (onCloseClick) {
                            onCloseClick();
                        }
                    }}
                >
                    <IconClose />
                </div>
            )}
        </div>
    );
};
